import { useCallback, useEffect, useRef, useState } from "react";
import { FiPause, FiPlay } from "react-icons/fi";
import { formatDuration } from "../config";

interface AudioPreviewProps {
  /** Audio URL */
  url: string;
  /** Filename for display */
  filename: string;
  /** Duration in seconds */
  duration?: number | null | undefined;
  /** Preview variant */
  variant: "inline" | "full";
  /** Optional className */
  className?: string | undefined;
}

/**
 * AudioPreview - Audio player with play/pause and progress
 *
 * Inline: Compact player for table rows
 * Full: Larger player for modals
 */
export function AudioPreview({
  url,
  filename,
  duration,
  variant,
  className = "",
}: AudioPreviewProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [isDragging, setIsDragging] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const inlineProgressRef = useRef<HTMLDivElement>(null);

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = useCallback(
    (percent: number) => {
      if (!audioRef.current) return;

      const newTime = percent * audioDuration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [audioDuration]
  );

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    handleSeek(percent);
  };

  // Drag handlers for inline progress bar
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      handleSeek(Math.max(0, Math.min(1, percent)));
    },
    [handleSeek]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !inlineProgressRef.current) return;

      const rect = inlineProgressRef.current.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      handleSeek(Math.max(0, Math.min(1, percent)));
    },
    [isDragging, handleSeek]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  if (variant === "inline") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          type="button"
          onClick={togglePlayPause}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FiPause className="h-4 w-4" /> : <FiPlay className="h-4 w-4 ml-0.5" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-gray-900">{filename}</p>
            <p className="shrink-0 text-xs text-gray-500">{formatDuration(audioDuration)}</p>
          </div>
          {/* Interactive progress bar with drag support */}
          <div
            ref={inlineProgressRef}
            className={`mt-1 h-2 flex-1 cursor-pointer overflow-hidden rounded-full bg-gray-200 ${isDragging ? "select-none" : ""}`}
            onMouseDown={handleMouseDown}
            role="slider"
            aria-label="Seek audio"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                handleSeek(Math.min(1, (currentTime + 5) / audioDuration));
              } else if (e.key === "ArrowLeft") {
                handleSeek(Math.max(0, (currentTime - 5) / audioDuration));
              }
            }}
          >
            <div
              className="pointer-events-none h-full rounded-full bg-blue-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* biome-ignore lint/a11y/useMediaCaption: Media preview displays user-uploaded content without captions */}
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />
      </div>
    );
  }

  // Full variant for modals
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlayPause}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FiPause className="h-6 w-6" /> : <FiPlay className="h-6 w-6 ml-1" />}
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-gray-900">{filename}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div
        ref={progressRef}
        onClick={handleProgressClick}
        className="h-2 cursor-pointer rounded-full bg-gray-200"
      >
        <div
          className="h-full rounded-full bg-blue-600 transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Time display */}
      <div className="flex justify-between text-sm text-gray-500">
        <span>{formatDuration(currentTime)}</span>
        <span>{formatDuration(audioDuration)}</span>
      </div>

      {/* biome-ignore lint/a11y/useMediaCaption: Media preview displays user-uploaded content without captions */}
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
    </div>
  );
}

export default AudioPreview;
